import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "./use-toast";

type Service = Tables<"services">;
type ServiceInsert = TablesInsert<"services">;
type ServiceUpdate = TablesUpdate<"services">;

interface ServiceWithStaff extends Service {
  assignedStaffIds?: string[];
  assignedLocationIds?: string[];
}

export function useServices() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const [services, setServices] = useState<ServiceWithStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    if (!business) {
      setServices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select("*")
        .eq("business_id", business.id)
        .eq("active", true)
        .order("created_at", { ascending: false });

      if (servicesError) throw servicesError;

      // Fetch staff assignments
      const { data: staffServices, error: staffError } = await supabase
        .from("staff_services")
        .select("service_id, staff_id");

      if (staffError) throw staffError;

      // Fetch location assignments
      const { data: serviceLocations, error: locationsError } = await supabase
        .from("service_locations")
        .select("service_id, location_id");

      if (locationsError) throw locationsError;

      // Map staff and locations to services
      const servicesWithStaff = (servicesData || []).map((service) => ({
        ...service,
        assignedStaffIds: staffServices
          ?.filter((ss) => ss.service_id === service.id)
          .map((ss) => ss.staff_id) || [],
        assignedLocationIds: serviceLocations
          ?.filter((sl) => sl.service_id === service.id)
          .map((sl) => sl.location_id) || [],
      }));

      setServices(servicesWithStaff);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch services");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const createService = async (
    serviceData: Omit<ServiceInsert, "business_id">,
    staffIds?: string[],
    locationIds?: string[]
  ) => {
    if (!business) throw new Error("No business found");

    const { data, error } = await supabase
      .from("services")
      .insert({ ...serviceData, business_id: business.id })
      .select()
      .single();

    if (error) throw error;

    // Assign staff if provided
    if (staffIds && staffIds.length > 0 && data) {
      const staffAssignments = staffIds.map((staffId) => ({
        service_id: data.id,
        staff_id: staffId,
      }));
      await supabase.from("staff_services").insert(staffAssignments);
    }

    // Assign locations if provided (empty array = available at all locations, so don't insert)
    if (locationIds && locationIds.length > 0 && data) {
      const locationAssignments = locationIds.map((locationId) => ({
        service_id: data.id,
        location_id: locationId,
      }));
      await supabase.from("service_locations").insert(locationAssignments);
    }

    toast({ title: "Service created", description: `${data.name} has been added.` });
    await fetchServices();
    return data;
  };

  const updateService = async (
    id: string,
    serviceData: ServiceUpdate,
    staffIds?: string[],
    locationIds?: string[]
  ) => {
    const { data, error } = await supabase
      .from("services")
      .update(serviceData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    // Update staff assignments
    if (staffIds !== undefined) {
      await supabase.from("staff_services").delete().eq("service_id", id);
      if (staffIds.length > 0) {
        const staffAssignments = staffIds.map((staffId) => ({
          service_id: id,
          staff_id: staffId,
        }));
        await supabase.from("staff_services").insert(staffAssignments);
      }
    }

    // Update location assignments
    if (locationIds !== undefined) {
      await supabase.from("service_locations").delete().eq("service_id", id);
      // Empty array = available at all locations, so don't insert anything
      if (locationIds.length > 0) {
        const locationAssignments = locationIds.map((locationId) => ({
          service_id: id,
          location_id: locationId,
        }));
        await supabase.from("service_locations").insert(locationAssignments);
      }
    }

    toast({ title: "Service updated", description: `${data.name} has been updated.` });
    await fetchServices();
    return data;
  };

  const archiveService = async (id: string) => {
    const { data, error } = await supabase
      .from("services")
      .update({ active: false })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    toast({ title: "Service archived", description: `${data.name} is now inactive and hidden from booking.` });
    await fetchServices();
    return data;
  };

  const deleteService = async (id: string) => {
    const { error } = await supabase.from("services").delete().eq("id", id);
    if (!error) {
      toast({ title: "Service deleted", description: "Service has been removed." });
      await fetchServices();
      return;
    }

    // If the service is referenced by existing appointments, Postgres blocks deletion (FK RESTRICT).
    // In that case, "archive" instead by setting active=false (keeps history intact).
    const errAny = error as any;
    const isConflict =
      errAny?.code === "23503" || // foreign_key_violation
      errAny?.status === 409 ||
      (typeof errAny?.message === "string" && errAny.message.toLowerCase().includes("foreign key"));

    if (isConflict) {
      const { error: archiveErr } = await supabase
        .from("services")
        .update({ active: false })
        .eq("id", id);
      if (archiveErr) throw archiveErr;

      toast({
        title: "Service archived",
        description: "This service has bookings, so it was archived (made inactive) instead of deleted.",
      });
      await fetchServices();
      return;
    }

    throw error;
  };

  return {
    services,
    loading,
    error,
    createService,
    updateService,
    archiveService,
    deleteService,
    refetch: fetchServices,
  };
}
